import React, { useContext, useEffect, useState } from "react";
import { styled } from "@mui/material/styles";

import { GlobalContext } from "@/helpers/contexts/contexts";
import { MUICustomAutocompleteSelect as MUICustomAutocompleteSelectBase } from "@/components/muiCustom/select/MUICustomAutocompleteSelect";
import SelectDownloadReportsBase from "@/components/pdf/SelectDownloadReports";
import PDFDownloadButtonBase from "@/components/pdf/PDFDownloadButton";
import { brandsWithCsvDownloadOption, USER_ROLE } from "@/config/constants";
import MUIDateRangeBase from "@/components/MUIDateRange";
import RpCustomMonthRangePickerBase from "@/components/datepicker/RpCustomMonthRangePicker";
import { isBrandHasDrillFeature } from "@/features/campaigns/shared/helpers/drillUpDownHelper";

type CampaignReportFiltersProps = {
  values: any;
  campaign?: any;
  setFieldValue: (_field: string, _value: any) => void;
  initialRange?: any;
};

type Option = {
  label: string;
  value: string | number;
};

const MUIDateRange = MUIDateRangeBase as React.ComponentType<any>;
const RpCustomMonthRangePicker =
  RpCustomMonthRangePickerBase as React.ComponentType<any>;
const MUICustomAutocompleteSelect =
  MUICustomAutocompleteSelectBase as React.ComponentType<any>;
const SelectDownloadReports =
  SelectDownloadReportsBase as React.ComponentType<any>;
const PDFDownloadButton = PDFDownloadButtonBase as React.ComponentType<any>;

const filterOptions = [
  { label: "Last Quarter", value: "last_quarter" },
  { label: "YTD", value: "ytd" },
  { label: "Custom", value: "custom" },
];
const StyledMUIDateRange = styled(MUIDateRange)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: theme?.palette?.primary?.main,
    padding: "5px 12px !important",
    fontFamily: "inherit",
    borderRadius: "var(--radius-control)",
    border: "none",
    textAlign: "center",
    marginBottom: 5,
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
    "& .MuiSvgIcon-root": {
      color: "var(--neutral-white)",
    },
  },
  "& .MuiInputBase-input": {
    fontSize: "10px !important",
    color: "var(--neutral-white) !important",
  },
  "& .MuiInputAdornment-root": {
    color: "var(--neutral-700) !important",
    "& .MuiSvgIcon-root": {
      color: "var(--neutral-white) !important",
    },
  },
}));
const CampaignReportFilters = ({
  values,
  campaign,
  setFieldValue,
  initialRange,
}: CampaignReportFiltersProps) => {
  const { loggedInUserRole, selectedBrand, loggedInUser } = useContext(
    GlobalContext
  ) as any;

  const [groupList, setGroupList] = useState<Option[]>([]);
  const [channelList, setChannelList] = useState<Option[]>([]);
  const [locationList, setLocationList] = useState<Option[]>([]);

  useEffect(() => {
    if (campaign && campaign?.group_list) {
      const groupList = campaign?.group_list.map((c: any) => ({
        label: c.group_name,
        value: c.groups_id,
      }));
      setGroupList(groupList);
    }
  }, [campaign]);

  useEffect(() => {
    if (campaign && campaign?.channel_list) {
      const channelList = campaign?.channel_list.map((c: any) => ({
        label: c.channel_name,
        value: c.channel_name,
      }));
      setChannelList(channelList);
    }
  }, [campaign]);

  useEffect(() => {
    if (campaign && campaign?.location_list) {
      const locationList = campaign?.location_list.map((c: any) => ({
        label: c.location_name,
        value: c.location_id,
      }));
      setLocationList(locationList);
    }
  }, [campaign]);

  const handleOnChangeRange = (range: any) => {
    setFieldValue("customRange", range);
  };

  return (
    <>
      <div className="row my-4 align-items-end campaign-filters">
        <div className="col-xl col-lg-4 col-md-6 d-flex flex-column justify-content-end mb-1 pr-xl-1">
          <MUICustomAutocompleteSelect
            name="filter"
            label="Report Period"
            selectProps={{
              onChange: (v: any) => {
                setFieldValue("filter", v);
              },
              value: values.filter,
            }}
            labelProps={{
              style: {
                color: "var(--neutral-700)",
              },
            }}
            options={filterOptions}
          />
        </div>
        {values.filter === "custom" ? (
          isBrandHasDrillFeature(selectedBrand?.system_generated_brand_id) ? (
            <div className="col-xl col-lg-4 col-md-6 px-xl-0 mb-1">
              <StyledMUIDateRange
                onChangeDate={handleOnChangeRange}
                label="Custom Date"
                isEndDateRequired={true}
                name="customRange"
                initialRange={values.customRange || initialRange}
              />
            </div>
          ) : (
            <div className="col-xl col-lg-4 col-md-6 px-xl-0 mb-3 mb-md-0">
              <RpCustomMonthRangePicker
                onChange={handleOnChangeRange}
                initialRange={values.customRange || initialRange}
                label="Custom Dates"
                labelProps={{
                  style: {
                    color: "var(--neutral-700)",
                    // marginTop: 5,
                  },
                }}
                displayButtonProps={{
                  style: {
                    fontSize: 10,
                    fontWeight: 600,
                    paddingLeft: "5px !important",
                    paddingRight: "5px !important",
                    marginBottom: 4,
                  },
                  size: "medium",
                }}
                buttonClassName="pl-2 pr-2"
              />
            </div>
          )
        ) : null}

        <div className="col-xl col-lg-4 col-md-6 my-1 px-xl-1">
          <MUICustomAutocompleteSelect
            name="channel_id"
            label="Channels"
            selectProps={{
              onChange: (v: any) => {
                setFieldValue("channel_id", v);
              },
              value: values.channel_id,
            }}
            labelProps={{
              style: {
                color: "var(--neutral-700)",
              },
            }}
            options={channelList}
            all={true}
          />
        </div>
        {loggedInUserRole !== USER_ROLE.PARTICIPANT.NAME && (
          <>
            <div className="col-xl col-lg-4 col-md-6 my-1 px-xl-1">
              <MUICustomAutocompleteSelect
                name="group_id"
                label="Groups"
                selectProps={{
                  onChange: (v: any) => {
                    setFieldValue("group_id", v);
                  },
                  value: values.group_id,
                }}
                labelProps={{
                  style: {
                    color: "var(--neutral-700)",
                  },
                }}
                options={groupList}
                all={true}
              />
            </div>
          </>
        )}

        <div className="col-xl col-lg-4 col-md-6 my-1 px-xl-1">
          <MUICustomAutocompleteSelect
            name="location_id"
            label="Locations"
            selectProps={{
              onChange: (v: any) => {
                setFieldValue("location_id", v);
              },
              value: values.location_id,
            }}
            labelProps={{
              style: {
                color: "var(--neutral-700)",
              },
            }}
            options={locationList}
            all={true}
          />
        </div>
        <div className="col-xl col-lg-4 col-md-6 d-flex align-items-end mb-1 mt-4 mt-md-4 px-4 pr-xl-3 pl-xl-2">
          {loggedInUser?.is_master_pass ||
          brandsWithCsvDownloadOption?.includes(
            selectedBrand?.system_generated_brand_id
          ) ? (
            <SelectDownloadReports
              title={`CAMPAIGN REPORT`}
              fileName={`${campaign?.campaign_name} Report`}
              campaign={campaign}
              values={values}
              initialRange={initialRange}
              elId="campaignReport"
            />
          ) : (
            <PDFDownloadButton
              label="Download PDF"
              elId="campaignReport"
              title={`CAMPAIGN REPORT`}
              fileName={`${campaign?.campaign_name}_Report`}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default CampaignReportFilters;
